import axios from 'axios';
import chunk from 'lodash-es/chunk';

import { ServerError } from '@apple/utils/api/errors';
import type { Guid } from '@apple/utils/primitive';
import { encodeSlashes } from '@apple/utils/url';

import {
	type CategoryLinesPaginatedList,
	categoryLinesPaginatedListSchema,
	type LineComment,
	type LineUpdateRequest,
	lineCommentUpdatesSchema,
	lineUpdateRequestSchema,
	type StoreEventItemErpDetail,
	type StoreEventLine,
	type StoreEventLineFilters,
	storeEventItemErpDetailSchema,
	storeEventLineResponseSchema,
	storeEventLineSchema,
} from '../models/lines';

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventItemQuantityApiController.GetItemGroups */
export async function getLinesByCategory(
	storeEventId: Guid,
	filter: StoreEventLineFilters,
	signal?: AbortSignal,
): Promise<CategoryLinesPaginatedList> {
	const response = await axios.get(`api/store-events/${storeEventId}/items`, {
		params: filter,
		signal,
	});

	return categoryLinesPaginatedListSchema.parse(response.data);
}

/**
 * @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventItemQuantityApiController.GetByCategoryProduct
 * This method is only used to check for locked items with the same product key for a category when manually adding a product
 */
export async function getLinesByCategoryAndProductId(
	storeEventId: Guid,
	categoryId: Guid,
	productItemId: string,
	signal?: AbortSignal,
): Promise<StoreEventLine[]> {
	const response = await axios.get(
		`api/store-events/${storeEventId}/items/category/${categoryId}/product/${encodeSlashes(productItemId)}`,
		{
			signal,
		},
	);

	return storeEventLineResponseSchema.parse(response.data).items;
}

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventItemQuantityApiController.Post */
export async function saveLines(storeEventId: Guid, request: LineUpdateRequest): Promise<void> {
	request = lineUpdateRequestSchema.parse(request);
	if (request.updates.length === 0) {
		return;
	}
	await axios.post(`api/store-events/${storeEventId}/items`, request);
}

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventItemDetailApiController.SaveComments */
export async function saveNotes(comments: LineComment[]): Promise<void> {
	const data = lineCommentUpdatesSchema.parse({ commentUpdates: comments });
	if (data.commentUpdates.length === 0) {
		return;
	}
	await axios.post('api/store-events/comments', data);
}

export async function getLineFulfillmentInfo(
	storeEventId: Guid,
	storeEventCategoryIds: Guid[],
	signal?: AbortSignal,
): Promise<StoreEventItemErpDetail[]> {
	const MAX_PARALLEL_REQUESTS = 4;

	if (storeEventCategoryIds.length === 0) return [];

	const batches = chunk(storeEventCategoryIds, MAX_PARALLEL_REQUESTS);
	const results: StoreEventItemErpDetail[] = [];

	for (const batch of batches) {
		const batchResults = await Promise.all(
			batch.map(async categoryId => {
				const result = await axios.get<StoreEventItemErpDetail[]>(
					`api/store-events/${storeEventId}/categories/${categoryId}/erp-details`,
					{ signal },
				);
				return storeEventItemErpDetailSchema.array().parse(result.data);
			}),
		);
		results.push(...batchResults.flat());
	}

	return results;
}

export async function syncItem({
	storeEventId,
	...params
}: {
	storeEventId: string;
	categoryId: string;
	productItemId: string;
	purchaseOrderNumber: string;
	externalLineItemNumber: string;
	storeEventItemId: string;
}): Promise<StoreEventLine | null> {
	const response = await axios.get(`/api/store-events/${storeEventId}/items/sync`, {
		params,
		validateStatus: status => [410, 400, 500].includes(status) || status < 300,
	});

	switch (response.status) {
		case 200:
			return storeEventLineSchema.parse(response.data);

		case 410:
			return null;

		//case 400:
		//case 500:
		default:
			throw new ServerError(response.data.message);
	}
}
